import {isNotNil} from 'ramda-adjunct';

import {getLevelIndicatorFlags, LevelIndicator} from '@dms/shared';

import {Nullish} from 'shared/web';

import {TestDriveDialogSchemaType} from '../TestDriveDialogSchema';

const FUEL_PERCENTAGE_MULTIPLIER = 100;

interface TestDriveLevelValues {
  fuelBefore: number | null;
  fuelAfter: number | null;
  batteryChargeBefore: number | null;
  batteryChargeAfter: number | null;
}

/**
 * @about Maps the test drive form's level sliders to the API payload. Every indicator the
 * vehicle does not have is sent as null so switching a vehicle clears the level that stopped
 * applying. Fuel is stored as a 0–1 fraction in the form and as a whole 0–100 percentage in the
 * API, so the fraction is rounded on the way out, battery charge is an integer percentage in both.
 */
export const getTestDriveLevelValues = (
  values: Pick<
    TestDriveDialogSchemaType,
    'fuelBefore' | 'fuelAfter' | 'batteryChargeBefore' | 'batteryChargeAfter'
  >,
  levelIndicators: LevelIndicator[]
): TestDriveLevelValues => {
  const {hasFuel, hasBattery} = getLevelIndicatorFlags(levelIndicators);

  const toFuelPercentage = (value: number | Nullish) =>
    hasFuel && isNotNil(value) ? Math.round(value * FUEL_PERCENTAGE_MULTIPLIER) : null;

  const toBatteryPercentage = (value: number | Nullish) =>
    hasBattery && isNotNil(value) ? value : null;

  return {
    fuelBefore: toFuelPercentage(values.fuelBefore),
    fuelAfter: toFuelPercentage(values.fuelAfter),
    batteryChargeBefore: toBatteryPercentage(values.batteryChargeBefore),
    batteryChargeAfter: toBatteryPercentage(values.batteryChargeAfter),
  };
};
